import { useEffect, useState } from 'react'
import { api, type QuadroResumo, type VisitaResumo } from '../api/cliente'
import { Cabecalho, Carregando, Erro } from '../componentes/Basicos'
import { Confirmar } from '../componentes/Confirmar'
import { FotosDoQuadro } from '../componentes/FotosDoQuadro'
import { emBr, momento } from '../datas'
import { salvar } from '../salvar'

/**
 * Os quadros de uma visita, os downloads, as fotos e o que apaga.
 *
 * **Nenhuma nota aparece aqui.** Nem do quadro, nem da visita. A nota é de quem abre a planilha, e
 * repeti-la na tela criaria uma segunda fonte que um dia diverge da fórmula. Também não há
 * resposta de questão, nem edição: corrigir quadro continua sendo do app de campo.
 *
 * **Apagar, sim.** Desde 02/09/2026, e com confirmação que diz o que se perde. Apagar não é
 * corrigir: leva a evidência junto, e não tem volta.
 */
export function Visita({ visitaId, aoVoltar }: { visitaId: string; aoVoltar: () => void }) {
  const [visita, setVisita] = useState<VisitaResumo | null>(null)
  const [quadros, setQuadros] = useState<QuadroResumo[] | null>(null)
  const [erro, setErro] = useState<unknown>(null)
  const [baixando, setBaixando] = useState<string | null>(null)
  const [recarga, setRecarga] = useState(0)

  const [fotosDe, setFotosDe] = useState<string | null>(null)
  const [aApagar, setAApagar] = useState<QuadroResumo | 'visita' | null>(null)
  const [apagando, setApagando] = useState(false)

  // `vivo` descarta o resultado que chega depois de a tela ter mudado de visita. Sem ele, uma
  // resposta lenta da visita anterior sobrescreveria a lista da atual — e a marca de laudo
  // afetado apareceria no quadro errado.
  useEffect(() => {
    let vivo = true

    Promise.all([api.visita(visitaId), api.quadros(visitaId)])
      .then(([aVisita, osQuadros]) => {
        if (!vivo) return
        setVisita(aVisita)
        setQuadros(osQuadros)
      })
      .catch((falha: unknown) => vivo && setErro(falha))

    return () => {
      vivo = false
    }
  }, [visitaId, recarga])

  /**
   * Baixar e recarregar, sempre. O download muda o estado do servidor — grava a data no quadro e
   * com isso limpa a marca —, então a lista na tela fica velha no instante em que o arquivo chega.
   */
  async function baixar(chave: string, buscar: () => Promise<{ blob: Blob; nome: string }>) {
    setBaixando(chave)
    setErro(null)
    try {
      const { blob, nome } = await buscar()
      salvar(blob, nome)
      setRecarga((n) => n + 1)
    } catch (falha) {
      setErro(falha)
    } finally {
      setBaixando(null)
    }
  }

  async function apagar() {
    if (!aApagar) return
    setApagando(true)
    setErro(null)
    try {
      if (aApagar === 'visita') {
        await api.apagarVisita(visitaId)
        aoVoltar()
        return
      }
      await api.apagarQuadro(aApagar.id)
      // O painel de fotos aberto era deste quadro: fechá-lo evita buscar foto que já não existe.
      if (fotosDe === aApagar.id) setFotosDe(null)
      setAApagar(null)
      setRecarga((n) => n + 1)
    } catch (falha) {
      setErro(falha)
      setAApagar(null)
    } finally {
      setApagando(false)
    }
  }

  if (!visita || !quadros) return <Carregando o="a visita" />

  const afetados = quadros.filter((q) => q.afetadoPorCorrecao)
  const baixados = quadros.filter((q) => q.laudoBaixadoEm)
  const ocupado = baixando !== null || apagando

  return (
    <>
      <Cabecalho
        titulo={visita.cliente}
        sub={`${visita.planta} · ${emBr(visita.data)} · ${quadros.length} ${
          quadros.length === 1 ? 'quadro fechado' : 'quadros fechados'
        }`}
        acoes={
          <>
            <button className="btn g sm" onClick={aoVoltar}>
              Visitas
            </button>
            {/* Sair estava só na lista: de dentro de uma visita era preciso voltar antes. */}
            <button className="btn g sm" onClick={() => api.sair()}>
              Sair
            </button>
          </>
        }
      />
      <div className="corpo">
        <div className="limite">
          <Erro erro={erro} aoFechar={() => setErro(null)} />

          {afetados.length > 0 && (
            <div className="aviso" role="status">
              <b>
                {afetados.length === 1
                  ? '1 laudo mudou depois de o bloco documental ser corrigido.'
                  : `${afetados.length} laudos mudaram depois de o bloco documental ser corrigido.`}
              </b>{' '}
              Quem recebeu o arquivo antes da correção está com uma versão velha na mão. Baixar de
              novo já entrega o laudo corrigido — a marca some sozinha.
              <div style={{ marginTop: 10 }}>
                <button
                  className="btn p sm"
                  disabled={ocupado}
                  onClick={() =>
                    baixar('afetados', () => api.baixarLaudosDaVisita(visitaId, true))
                  }
                >
                  {baixando === 'afetados' ? 'gerando…' : `Baixar os ${afetados.length} afetados`}
                </button>
              </div>
            </div>
          )}

          <div className="barra">
            {quadros.length > 0 && (
              <button
                className="btn"
                disabled={ocupado}
                onClick={() => baixar('visita', () => api.baixarLaudosDaVisita(visitaId))}
              >
                {baixando === 'visita' ? 'gerando…' : 'Baixar a visita inteira (.zip)'}
              </button>
            )}
            <button className="btn g" disabled={ocupado} onClick={() => setAApagar('visita')}>
              Apagar a visita
            </button>
          </div>

          {quadros.length === 0 ? (
            <div className="vazio">
              Esta visita ainda não tem quadro fechado. Os quadros nascem no app de campo.
            </div>
          ) : (
            <ul className="lista">
              {quadros.map((quadro) => (
                <li key={quadro.id} className={quadro.afetadoPorCorrecao ? 'item marcado' : 'item'}>
                  <div className="dados">
                    <div className="linha1">
                      <b>{quadro.tag}</b>
                      <span className="setor">{quadro.setor}</span>
                      {quadro.afetadoPorCorrecao && <span className="selo">laudo afetado</span>}
                    </div>
                    <div className="linha2">
                      {quadro.naoConformidades === 0
                        ? 'sem não conformidade'
                        : `${quadro.naoConformidades} ${
                            quadro.naoConformidades === 1
                              ? 'não conformidade'
                              : 'não conformidades'
                          }`}
                      {' · '}
                      fechado em {momento(quadro.fechadoEm)}
                    </div>
                    <Marca quadro={quadro} />

                    {fotosDe === quadro.id && <FotosDoQuadro quadroId={quadro.id} />}
                  </div>

                  <div className="acoesDoItem">
                    <button
                      className="btn g sm"
                      aria-expanded={fotosDe === quadro.id}
                      onClick={() => setFotosDe(fotosDe === quadro.id ? null : quadro.id)}
                    >
                      {fotosDe === quadro.id ? 'Fechar fotos' : 'Fotos'}
                    </button>
                    <button
                      className="btn sm"
                      disabled={ocupado}
                      onClick={() => baixar(quadro.id, () => api.baixarLaudo(quadro.id))}
                    >
                      {baixando === quadro.id ? 'gerando…' : 'Baixar .xlsx'}
                    </button>
                    <button
                      className="btn g sm"
                      disabled={ocupado}
                      onClick={() => setAApagar(quadro)}
                    >
                      Apagar
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {aApagar === 'visita' && (
        <Confirmar
          titulo="Apagar a visita inteira?"
          rotulo="Apagar a visita inteira"
          ocupado={apagando}
          aoConfirmar={apagar}
          aoCancelar={() => setAApagar(null)}
        >
          <p>
            <b>
              {visita.cliente} · {visita.planta} · {emBr(visita.data)}
            </b>
          </p>
          <p>
            Somem{' '}
            {quadros.length === 1
              ? 'o quadro fechado'
              : `os ${quadros.length} quadros fechados`}
            , o bloco documental e <b>todas as fotos</b> — do banco e do disco do servidor.
          </p>
          <p>
            Não existe arquivo de laudo guardado: cada planilha é gerada do banco na hora do
            download. Apagar a visita apaga os laudos dela, e <b>não tem volta</b>.
          </p>
          {baixados.length > 0 && (
            <p className="alerta">
              {baixados.length === 1
                ? '1 laudo desta visita já foi baixado e pode estar com o cliente.'
                : `${baixados.length} laudos desta visita já foram baixados e podem estar com o cliente.`}{' '}
              A evidência que os sustenta some aqui.
            </p>
          )}
        </Confirmar>
      )}

      {aApagar !== null && aApagar !== 'visita' && (
        <Confirmar
          titulo="Apagar este quadro?"
          rotulo="Apagar o quadro"
          ocupado={apagando}
          aoConfirmar={apagar}
          aoCancelar={() => setAApagar(null)}
        >
          <p>
            <b>{aApagar.tag}</b> · {aApagar.setor}
          </p>
          <p>
            Somem as respostas, as não conformidades e <b>as fotos do quadro</b> — do banco e do
            disco do servidor. A planilha dele deixa de existir: não há arquivo guardado, o laudo é
            gerado do banco a cada download. <b>Não tem volta.</b>
          </p>
          {aApagar.laudoBaixadoEm && (
            <p className="alerta">
              O laudo deste quadro foi baixado em {momento(aApagar.laudoBaixadoEm)} e pode estar com
              o cliente. A evidência que o sustenta some aqui.
            </p>
          )}
        </Confirmar>
      )}
    </>
  )
}

/**
 * A frase com as duas datas: baixado em tal dia, corrigido em tal dia. É o que o auditor precisa
 * para decidir se reenvia ao cliente — **a marca informa, não bloqueia**.
 *
 * O quadro marcado que nunca foi baixado diz isso com todas as letras. Ele carrega a marca sem
 * consequência: não existe arquivo velho na mão de ninguém.
 */
function Marca({ quadro }: { quadro: QuadroResumo }) {
  if (!quadro.afetadoPorCorrecao) {
    if (!quadro.laudoBaixadoEm) return null
    return <div className="linha3">baixado em {momento(quadro.laudoBaixadoEm)}</div>
  }

  return (
    <div className="linha3 alerta">
      {quadro.laudoBaixadoEm
        ? `baixado em ${momento(quadro.laudoBaixadoEm)}, e o bloco documental foi corrigido em ${momento(quadro.blocoCorrigidoEm!)}`
        : `nunca baixado, e o bloco documental foi corrigido em ${momento(quadro.blocoCorrigidoEm!)}`}
    </div>
  )
}
